import { CloseIcon } from '@chakra-ui/icons'
import { Flex, HStack, IconButton } from '@chakra-ui/react'
import { DateField } from '@hb/shared'
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { InputElement, InputProps } from '../../../../types/fields'
import { getInputContainerPadding } from '../Shared/InputContainer'
import { useInputSize } from '../Shared/sizes'
import { baseInputStyle } from '../Shared/styles'
import { SetToNowButton } from './SetToNowButton'
import { useDefaultToNow } from './hooks'

export const NativeDateInput: InputElement<DateField> = forwardRef<
  { focus: () => void },
  InputProps<DateField>
>((props, ref) => {
  const {
    input,
    field,
    style,
    disabled,
    readOnly,
    meta: { active },
  } = props
  const {
    placeholder,
    // maxDate,
    optional,
    isoFormat,
    withNowButton,
  } = field
  const { onChange, value, onFocus, onBlur } = input

  useDefaultToNow(field, value, onChange)

  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
  }))

  const size = useInputSize(props.size ?? field?.size)
  const isCompact = size === 'xs' || size === 'sm'
  const inputValue = useMemo(() => {
    if (!value) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number') return new Date(value).toISOString().split('T')[0]
    return ''
  }, [value])

  const placeholderWithOptional = useMemo(() => {
    if (optional) return `${placeholder} (optional)`
    return placeholder
  }, [optional, placeholder])

  return (
    <Flex align="center" gap={2}>
      <HStack
        spacing={2}
        {...getInputContainerPadding(size)}
        cursor="pointer"
        minW={isCompact ? '0' : '270px'}
        minH="36px"
        background="white"
        boxShadow={`1px 1px 3px ${active ? 'rgba(0, 0, 100, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`}
        border={isCompact ? undefined : '1px solid #cdcdcd'}
        _hover={{ background: 'gray.50' }}
        borderRadius={6}
        color={value ? 'gray.700' : 'gray.500'}
        style={style}>
        <input
          ref={inputRef}
          value={inputValue}
          placeholder={placeholderWithOptional}
          onChange={e => {
            const { value: updatedValue } = e.target
            if (!updatedValue) return
            const updated = new Date(updatedValue)
            if (Number.isNaN(updatedValue)) {
              onChange(undefined)
            } else {
              onChange(isoFormat ? updatedValue : updated?.getTime())
            }
          }}
          style={{
            ...baseInputStyle(size, disabled),
            flex: 1,
            background: 'transparent',
          }}
          type="date"
          disabled={disabled}
          readOnly={readOnly}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {value && optional ? (
          <IconButton
            ml="auto"
            size="xs"
            width="20px"
            minW="0"
            height="20px"
            bg="gray.400"
            borderRadius="full"
            color="white"
            tabIndex={-1}
            aria-label="Clear"
            icon={<CloseIcon color="gray.100" w={2} h={2} />}
            onClick={e => {
              e.stopPropagation()
              onChange(undefined)
            }}
          />
        ) : null}
      </HStack>
      {withNowButton ? (
        <SetToNowButton field={field} onChange={onChange} disabled={disabled} readOnly={readOnly} />
      ) : null}
    </Flex>
  )
})
